import type { PharmaVariantMetadata } from '@lib/types/pharma';

export type VariantForBuyability = {
  metadata?: Record<string, unknown> | null;
};

/**
 * Why a variant can't be bought, or null when it's sellable:
 *   - 'not-for-sale'           — flagged not_for_sale (carried over for SEO,
 *                                never buyable regardless of stock/classification).
 *   - 'missing-classification' — no legal_classification at all.
 *   - 'missing-questionnaire'  — P/POM classification but no questionnaire_id,
 *                                so the consultation flow can't run.
 *
 * The single source of truth for "can this variant be bought", consumed by
 * the PDP CTA (via @modules/products/utils/requires-consultation), JSON-LD
 * availability, and the Google product feed. Pure and server-safe — no
 * React/mount-state dependency — so every consumer can call it directly.
 *
 * An absent variant is deliberately unbuyable (fail-safe: the feed and JSON-LD
 * always have a variant, so a missing one means broken data, not a sellable
 * default). The reason reported for it is an artefact — there's no metadata to
 * classify — so a consumer where "nothing selected yet" is a legitimate state
 * must guard on the variant itself before reading the reason. `getCTAState`
 * and `isBlockedByMetadata` both do.
 */
export type BuyabilityBlockedReason =
  | 'not-for-sale'
  | 'missing-classification'
  | 'missing-questionnaire';

/** Classifications sellable straight from the PDP, no consultation needed. */
const DIRECT_SALE_CLASSIFICATIONS = new Set(['GSL', 'OTHER']);
/** Classifications that must route through a consultation to be sold. */
const CONSULTATION_CLASSIFICATIONS = new Set(['P', 'POM']);

export function getBuyabilityBlockedReason(
  variant: VariantForBuyability | undefined | null,
): BuyabilityBlockedReason | null {
  if (!variant) {
    return 'missing-classification';
  }
  const meta = variant.metadata as PharmaVariantMetadata | undefined;
  if (meta?.not_for_sale === true) {
    return 'not-for-sale';
  }
  const classification = meta?.legal_classification;
  if (!classification) {
    return 'missing-classification';
  }
  if (DIRECT_SALE_CLASSIFICATIONS.has(classification)) {
    return null;
  }
  // Anything we don't recognise counts as unclassified. Metadata reaches us as
  // untyped JSON from Medusa, so a stray value ('CD', 'gsl', 'GSL ') must
  // default to deny rather than fall through to the consultation branch and
  // read as sellable — which is also what the PDP CTA renders for it.
  if (!CONSULTATION_CLASSIFICATIONS.has(classification)) {
    return 'missing-classification';
  }
  // P or POM — must route through a consultation, which needs a questionnaire_id.
  if (!meta.questionnaire_id) {
    return 'missing-questionnaire';
  }
  return null;
}

export function isVariantBuyable(
  variant: VariantForBuyability | undefined | null,
): boolean {
  return getBuyabilityBlockedReason(variant) === null;
}
